import React, { useCallback, useEffect, useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import Base from '../Base';
import Card from '../../components/elements/Card';
import Title from '../../components/common/Title';
import Back from '../../components/common/Back';
import Icon from '../../components/common/Icon';
import { apiRequest } from '../../utils';
import './MemberHome.css';

/* Bravos Company Signal Record — the contact person declares their company's firmographic
   signals (the co_* fields that feed the methodology's Company-Signals scoring). An editable
   profile (revisit + update any time), reached from a badge on /member-home (contact view).
   GET /api/bravos/company-signal returns {values, prefill, completion}; POST replaces the blob.
   Contact-person only server-side — a member hitting the URL is bounced to /member-home. */

// Option tokens — MUST match shared_models/mutations/bravos_company_signal.py. Labels are
// rendered from these via t('cso_<token>').
const SECTORS = ['agriculture', 'manufacturing', 'construction_real_estate', 'wholesale_retail',
  'transport_logistics', 'hospitality_tourism', 'ict', 'financial_services',
  'professional_services', 'education', 'healthcare', 'energy_utilities', 'media_creative',
  'public_sector', 'other'];
const SIZE_BANDS = ['micro', 'small', 'medium', 'large'];
const BUSINESS_MODELS = ['b2b', 'b2c', 'b2b2c', 'b2g', 'marketplace', 'other'];
const EMIRATES = ['abu_dhabi', 'dubai', 'sharjah', 'ajman', 'umm_al_quwain', 'ras_al_khaimah',
  'fujairah'];
const REVENUE_BANDS = ['lt_3m', '3m_10m', '10m_50m', '50m_250m', 'gt_250m', 'undisclosed'];
const LANGUAGES = ['english', 'arabic', 'both', 'other'];
const SDGS = Array.from({ length: 17 }, (_, i) => `sdg${i + 1}`);

// Field layout: grouped sections. `t` = widget type (matches the backend spec).
const SECTIONS = [
  { h: 'csSecIdentity', fields: [
    { k: 'co_legal_name', t: 'text', label: 'csLegalName' },
    { k: 'co_trading_name', t: 'text', label: 'csTradingName' },
    { k: 'co_trade_license_nr', t: 'text', label: 'csTradeLicense' },
    { k: 'co_trn', t: 'text', label: 'csTrn' },
  ] },
  { h: 'csSecClassification', fields: [
    { k: 'co_sector', t: 'select', options: SECTORS, label: 'csSector' },
    { k: 'co_sub_sector', t: 'text', label: 'csSubSector' },
    { k: 'co_size_band', t: 'select', options: SIZE_BANDS, label: 'csSizeBand' },
    { k: 'co_business_model', t: 'select', options: BUSINESS_MODELS, label: 'csBusinessModel' },
  ] },
  { h: 'csSecLocation', fields: [
    { k: 'co_emirate', t: 'select', options: EMIRATES, label: 'csEmirate' },
    { k: 'co_founding_year', t: 'year', label: 'csFoundingYear' },
    { k: 'co_ua_presence_since', t: 'year', label: 'csUaePresenceSince' },
    { k: 'co_countries_count', t: 'int', label: 'csCountriesCount' },
  ] },
  { h: 'csSecWorkforce', fields: [
    { k: 'co_employee_count', t: 'int', label: 'csEmployeeCount' },
    { k: 'co_blue_collar_count', t: 'int', label: 'csBlueCollarCount' },
    { k: 'co_emirati_count', t: 'int', label: 'csEmiratiCount' },
    { k: 'co_women_percent', t: 'percent', label: 'csWomenPercent' },
    { k: 'co_highly_educated_percent', t: 'percent', label: 'csHighlyEducatedPercent' },
    { k: 'co_nationalities_count', t: 'int', label: 'csNationalitiesCount' },
  ] },
  { h: 'csSecMarket', fields: [
    { k: 'co_customer_count', t: 'int', label: 'csCustomerCount' },
    { k: 'co_revenue_band', t: 'select', options: REVENUE_BANDS, label: 'csRevenueBand' },
    { k: 'co_language_preference', t: 'select', options: LANGUAGES, label: 'csLanguagePreference' },
  ] },
  { h: 'csSecPurpose', fields: [
    // ★ READ-ONLY here. Both live in their own long-standing tables (data.PartnerValues,
    // data.SDGForPartner) and are edited on Programmes ▸ Values / SDGs — a value carries a
    // picture and a goal carries a reason, neither of which a flat field on this form can
    // hold. Editing them in two places would have meant two stores drifting apart, so this
    // screen shows what is declared and points at the one place that writes it.
    { k: 'co_declared_values', t: 'list', label: 'csDeclaredValues', readOnly: true,
      hint: 'csDeclaredValuesHint' },
    { k: 'co_sdg_alignment', t: 'multiselect', options: SDGS, label: 'csSdgAlignment',
      readOnly: true },
    { k: 'co_growth_target_2033', t: 'number', label: 'csGrowthTarget2033', hint: 'csGrowthHint' },
    { k: 'co_growth_target_2033_outside_uae', t: 'number', label: 'csGrowthTarget2033OutsideUae', hint: 'csGrowthHint' },
  ] },
];

const NUMERIC = { int: true, percent: true, year: true, number: true };

// Keep only the co_* fields (drop co_company_id + the _updated_* stamps) for the editable form.
const stripMeta = (obj) => {
  const out = {};
  Object.keys(obj || {}).forEach((k) => {
    if (k.startsWith('co_') && k !== 'co_company_id') out[k] = obj[k];
  });
  return out;
};

const CompanySignal = () => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const [form, setForm] = useState(null);       // null until loaded
  const [draft, setDraft] = useState({});        // per-list tag input drafts
  const [saving, setSaving] = useState(false);
  const [saved, setSaved] = useState(false);
  const [error, setError] = useState(null);

  const load = useCallback(async () => {
    try {
      const res = await apiRequest('GET', '/bravos/company-signal');
      // Prefill seeds only EMPTY fields — saved values win.
      const merged = { ...(res.prefill || {}), ...stripMeta(res.values) };
      setForm(merged);
    } catch {
      navigate('/member-home');
    }
  }, [navigate]);

  useEffect(() => { load(); }, [load]);

  const setField = (k, v) => {
    setSaved(false);
    setForm((f) => ({ ...f, [k]: v }));
  };
  const toggleToken = (k, token) => {
    setSaved(false);
    setForm((f) => {
      const cur = Array.isArray(f[k]) ? f[k] : [];
      return { ...f, [k]: cur.includes(token) ? cur.filter((x) => x !== token) : [...cur, token] };
    });
  };
  const addTag = (k) => {
    const v = (draft[k] || '').trim();
    if (!v) return;
    setSaved(false);
    setForm((f) => {
      const cur = Array.isArray(f[k]) ? f[k] : [];
      return cur.includes(v) ? f : { ...f, [k]: [...cur, v] };
    });
    setDraft((d) => ({ ...d, [k]: '' }));
  };
  const removeTag = (k, i) => {
    setSaved(false);
    setForm((f) => ({ ...f, [k]: (f[k] || []).filter((_, idx) => idx !== i) }));
  };

  const save = async () => {
    if (saving) return;
    setSaving(true); setError(null); setSaved(false);
    try {
      const res = await apiRequest('POST', '/bravos/company-signal', { values: form });
      setForm(stripMeta(res && res.values ? res.values : form));
      setSaved(true);
      window.scrollTo(0, 0);
    } catch {
      setError(t('csSaveError'));
    } finally {
      setSaving(false);
    }
  };

  const renderField = (f) => {
    const val = form[f.k];
    if (f.readOnly) {
      // The same chips and tags, minus the handlers — so the record still READS whole, and the
      // only difference is that this screen cannot change them.
      const cur = Array.isArray(val) ? val : [];
      return (
        <>
          {cur.length === 0 ? (
            <p className="cs-hint mb-0">{t('csNotDeclaredYet')}</p>
          ) : (
            <div className="cs-tags">
              {cur.map((v, i) => (
                <span key={i} className="cs-tag cs-tag--ro">
                  {f.t === 'multiselect' ? t(`cso_${v}`) : v}
                </span>
              ))}
            </div>
          )}
          {/* ★ Field-aware. Both of these are shown here read-only and edited elsewhere,
              but they are edited on two DIFFERENT screens now — one button for both would send
              half the clicks to the wrong one. */}
          <button type="button" className="cs-editlink"
                  onClick={() => navigate(f.k === 'co_sdg_alignment' ? '/sdgs' : '/values')}>
            <Icon name="pencil" />
            {f.k === 'co_sdg_alignment' ? t('csEditOnSdgs') : t('csEditOnValues')}
          </button>
        </>
      );
    }
    if (f.t === 'select') {
      return (
        <select className="form-select" value={val || ''}
                onChange={(e) => setField(f.k, e.target.value)}>
          <option value="">{t('csSelectPlaceholder')}</option>
          {f.options.map((o) => <option key={o} value={o}>{t(`cso_${o}`)}</option>)}
        </select>
      );
    }
    if (f.t === 'multiselect') {
      const cur = Array.isArray(val) ? val : [];
      return (
        <div className="cs-chip-row">
          {f.options.map((o) => (
            <button type="button" key={o}
                    className={`cs-chip ${cur.includes(o) ? 'cs-chip--on' : ''}`}
                    onClick={() => toggleToken(f.k, o)}>
              {t(`cso_${o}`)}
            </button>
          ))}
        </div>
      );
    }
    if (f.t === 'list') {
      const cur = Array.isArray(val) ? val : [];
      return (
        <>
          {cur.length > 0 && (
            <div className="cs-tags">
              {cur.map((v, i) => (
                <span key={i} className="cs-tag">
                  {v}
                  <button type="button" onClick={() => removeTag(f.k, i)}>&times;</button>
                </span>
              ))}
            </div>
          )}
          <input className="form-control" value={draft[f.k] || ''}
                 onChange={(e) => setDraft((d) => ({ ...d, [f.k]: e.target.value }))}
                 onKeyDown={(e) => { if (e.key === 'Enter') { e.preventDefault(); addTag(f.k); } }}
                 placeholder={t('csTagPlaceholder')} />
        </>
      );
    }
    return (
      <input className="form-control"
             type={NUMERIC[f.t] ? 'number' : 'text'}
             min={NUMERIC[f.t] ? 0 : undefined}
             value={val === undefined || val === null ? '' : val}
             onChange={(e) => setField(f.k, e.target.value)} />
    );
  };

  return (
    <Base activeNav="home">
      <Card>
        <Back to="/member-home" />
        <Title my="1" className="mt-3">
          <Icon name="building" theme="primary" className="me-2" />
          {t('csTitle')}
        </Title>
        <p className="text-muted text-start">
          {t('csIntro')}{' '}
          {/* Name, logo and the legal documents live on Edit company, not in this record. */}
          <Link to="/edit-company" className="fw-semibold">{t('csEditCompanyLink')}</Link>
        </p>
        <p className="cs-privacy text-start">
          <Icon name="shield-check" className="me-1" />
          {t('csPrivacy')}
        </p>

        {form === null ? (
          <span className="auth__spinner" />
        ) : (
          <>
            {saved && (
              <div className="cs-saved"><Icon name="check" /> {t('csSaved')}</div>
            )}
            {SECTIONS.map((sec) => (
              <div key={sec.h} className="cs-section">
                <p className="cs-section__h">{t(sec.h)}</p>
                {sec.fields.map((f) => (
                  <div key={f.k} className="cs-field">
                    <label className="form-label">{t(f.label)}</label>
                    {renderField(f)}
                    {f.hint && <p className="cs-hint">{t(f.hint)}</p>}
                  </div>
                ))}
              </div>
            ))}

            {error && <p className="text-danger mt-2">{error}</p>}
            <button type="button" className="bravos-submit" onClick={save} disabled={saving}>
              {saving ? <span className="auth__spinner" /> : <Icon name="disk" />}
              {t('csSave')}
            </button>
          </>
        )}
      </Card>
    </Base>
  );
};

export default CompanySignal;
